import { useState, useCallback } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import type { ApiResponse } from '../types';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  fetchData: () => Promise<void>;
}

/**
 * Base API hook with loading/error states
 * @param url - API endpoint URL
 * @param options - Optional fetch options
 */
export function useApi<T>(
  url: string,
  options?: RequestInit
): UseApiState<T> {
  const authenticatedFetch = useAuthenticatedFetch();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(url, options);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json: ApiResponse<T> = await response.json();

      if (json.success && json.data) {
        setData(json.data);
      } else {
        throw new Error(json.error || 'Unknown API error');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch data';
      setError(message);
      console.error('API error:', err);
    } finally {
      setLoading(false);
    }
  }, [url, options, authenticatedFetch]);

  return { data, loading, error, fetchData };
}
